import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Select, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import styled, { createGlobalStyle } from 'styled-components';
import { useDispatch } from 'react-redux';
import { Box, Flex, Text } from 'atoms';
import { getVehicleTypeByIdV2, updateVehicleTypeV2 } from 'store/vehicleSliceV2';

const { Option } = Select;

const VEHICLE_TYPE_LIST_ROUTE = '/vehicle-type';

// Global styles for Select dropdown
const GlobalSelectStyle = createGlobalStyle`
	.ant-select-dropdown .ant-select-item {
		color: #374151;
		padding: 8px 12px;
		
		&.ant-select-item-option-selected {
			background-color: #e0f2fe;
		}
		
		&:hover {
			background-color: #f3f4f6;
		}
	}
	
	.ant-select-dropdown {
		z-index: 1050;
	}
`;

const PageContainer = styled(Box)`
	background: #f8fafc;
	min-height: 100vh;
	padding: 24px;
`;

const ContentWrapper = styled(Box)`
	max-width: 1400px;
	margin: 0 auto;
`;

const PageHeader = styled(Flex)`
	margin-bottom: 24px;
	align-items: center;
`;

const BackButton = styled(Box)`
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 8px;
	border-radius: 6px;
	transition: background-color 0.2s;

	&:hover {
		background-color: #f1f5f9;
	}
`;

const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #0f172a;
	line-height: 1.2;
`;

const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #64748b;
	margin-top: 4px;
`;

const EditButton = styled.button`
	background-color: #18405d;
	border: 1px solid ##18405d;
	color: white;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	// border-radius: 6px;
	cursor: pointer;
	font-size: 14px;
	transition: all 0.2s;

	&:hover {
		background-color: #1e3a8a;
		border-color: #1e3a8a;
	}

	&:disabled {
		background-color: #94a3b8;
		border-color: #94a3b8;
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

const FormSection = styled(Box)`
	background: white;
	padding: 32px;
	border-radius: 12px;
	// border: 2px solid #3b82f6;
	margin-bottom: 20px;
`;

const StyledForm = styled(Form)`
	.ant-form-item-label > label {
		font-weight: 500;
		font-size: 14px;
		color: #1e293b;
	}

	.ant-form-item {
		margin-bottom: 32px;
	}

	.ant-input,
	.ant-select-selector,
	.ant-input-textarea {
		border-radius: 6px;
		border: 1px solid #e2e8f0;
		font-size: 14px;
		background: #f9fafb;

		&:hover {
			border-color: #94a3b8;
		}

		&:focus,
		&.ant-input-focused,
		&.ant-select-focused .ant-select-selector {
			border-color: #3b82f6;
			box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
		}
	}

	.ant-select-selector {
		height: 40px !important;
		padding: 4px 11px !important;
		display: flex;
		align-items: center;
		background: #f9fafb !important;
	}

	.ant-input {
		height: 40px;
		padding: 8px 12px;
		background: #f9fafb;
	}

	.ant-input-textarea {
		padding: 8px 12px;
	}
`;

const ButtonContainer = styled(Flex)`
	justify-content: flex-end;
	gap: 12px;
	margin-top: 24px;
`;

const CancelButton = styled.button`
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	border: 1px solid #d1d5db;
	color: #374151;
	background: white;
	border-radius: 6px;
	cursor: pointer;
	font-size: 14px;
	transition: all 0.2s;

	&:hover:not(:disabled) {
		border-color: #9ca3af;
		color: #1f2937;
		background: #f9fafb;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		background: #f9fafb;
		color: #9ca3af;
		border-color: #e5e7eb;
	}
`;

const SaveButton = styled.button`
	background-color: #18405d;
	border: 1px solid #18405d;
	height: 40px;
	padding: 0 32px;
	font-weight: 500;
	font-size: 14px;
	color: white;
	// border-radius: 6px;
	cursor: pointer;
	transition: all 0.2s;

	&:hover:not(:disabled) {
		background-color: #1e3a8a;
		border-color: #1e3a8a;
	}

	&:disabled {
		background-color: #94a3b8;
		border-color: #94a3b8;
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

function EditVehicleTypePageV2() {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [initialLoading, setInitialLoading] = useState(true);
	const [isEditMode, setIsEditMode] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();

	const fetchVehicleTypeDetails = useCallback(async () => {
		setInitialLoading(true);
		try {
			const response = await dispatch(
				getVehicleTypeByIdV2({
					id,
				})
			);

			if (response && typeof response === 'object') {
				// Populate form with fetched data
				form.setFieldsValue({
					maker: response.maker_manufacturer,
					modelName: response.vehicle_type_name,
					capacity: response.capacity_tonnes,
					length: response.length_feet,
					tempControlled: response.temperature_controlled ? 'true' : 'false',
					driverMode: response.driver_mode,
					fuelType: response.fuel_type,
					description: response.description,
				});
			} else {
				message.error('Vehicle type not found');
				navigate(VEHICLE_TYPE_LIST_ROUTE);
			}
		} catch (error) {
			console.error('Error fetching vehicle type:', error);
			message.error('Failed to load vehicle type details');
			navigate(VEHICLE_TYPE_LIST_ROUTE);
		} finally {
			setInitialLoading(false);
		}
	}, [id, dispatch, form, navigate]);

	useEffect(() => {
		if (id) {
			fetchVehicleTypeDetails();
		}
	}, [id, fetchVehicleTypeDetails]);

	const handleBack = () => {
		navigate(VEHICLE_TYPE_LIST_ROUTE);
	};

	const handleEdit = () => {
		setIsEditMode(true);
	};

	const handleCancelEdit = () => {
		setIsEditMode(false);
		// Reset form to original values
		fetchVehicleTypeDetails();
	};

	const handleSubmit = async () => {
		setLoading(true);
		try {
			const values = await form.validateFields();

			const updateData = {
				id,
				vehicle_type_name: values.modelName,
				maker_manufacturer: values.maker,
				capacity_tonnes: Number(values.capacity),
				length_feet: Number(values.length),
				temperature_controlled: values.tempControlled === 'true',
				driver_mode: values.driverMode || null,
				fuel_type: values.fuelType || null,
				description: values.description || null,
			};

			const response = await dispatch(
				updateVehicleTypeV2({
					req_data: updateData,
					setErrors: () => {},
				})
			);

			if (response && response.status === 'success') {
				message.success('Vehicle type updated successfully');
				// Navigate back to list page after successful save
				setTimeout(() => {
					navigate(VEHICLE_TYPE_LIST_ROUTE);
				}, 1000); // Small delay to show success message
			} else {
				const errorMessage = response?.message || 'Failed to update vehicle type';
				message.error(errorMessage);
			}
		} catch (error) {
			console.error('Update error:', error);
			if (error.errorFields) {
				message.error('Please fill in all required fields correctly.');
			} else {
				message.error('An error occurred while updating vehicle type');
			}
		} finally {
			setLoading(false);
		}
	};

	if (initialLoading) {
		return (
			<PageContainer>
				<ContentWrapper>
					<Flex
						justifyContent="center"
						alignItems="center"
						style={{ minHeight: '400px' }}
					>
						<Text fontSize="16px" color="#64748b">
							Loading vehicle type details...
						</Text>
					</Flex>
				</ContentWrapper>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<GlobalSelectStyle />
			<ContentWrapper>
				<PageHeader justifyContent="space-between">
					<Flex alignItems="center">
						<BackButton onClick={handleBack}>
							<ArrowLeftOutlined style={{ fontSize: '20px', color: '#64748b' }} />
						</BackButton>
						<Box ml={3}>
							<PageTitle>Vehicles</PageTitle>
							<PageSubtitle>Edit Vehicle Type</PageSubtitle>
						</Box>
					</Flex>
					{!isEditMode && <EditButton onClick={handleEdit}>Edit</EditButton>}
				</PageHeader>

				<FormSection>
					<StyledForm form={form} layout="vertical">
						<Flex gap="32px" flexWrap="wrap">
							<Box flex="1" minWidth="300px">
								<Form.Item
									label="Maker/Manufacturer"
									name="maker"
									rules={[{ required: true, message: 'Please enter maker' }]}
								>
									{/* Dropdown - commented out for now
									<Select
										placeholder="Select"
										size="large"
										getPopupContainer={(trigger) => trigger.parentNode}
										disabled={!isEditMode}
									>
										<Option value="TATA">TATA</Option>
										<Option value="Tata Motors">Tata Motors</Option>
										<Option value="Mahindra">Mahindra</Option>
										<Option value="Ashoka Leyland">Ashoka Leyland</Option>
										<Option value="Eicher">Eicher</Option>
										<Option value="Ola Electric">Ola Electric</Option>
										<Option value="Piaggio">Piaggio</Option>
										<Option value="Atul Auto">Atul Auto</Option>
										<Option value="ALTIGREEN">ALTIGREEN</Option>
									</Select>
									*/}
									<Input
										placeholder="Enter maker"
										size="large"
										disabled={!isEditMode}
									/>
								</Form.Item>
							</Box>

							<Box flex="1" minWidth="300px">
								<Form.Item
									label="Model Name"
									name="modelName"
									rules={[{ required: true, message: 'Please enter model name' }]}
								>
									<Input
										placeholder="Enter model name"
										size="large"
										disabled={!isEditMode}
									/>
								</Form.Item>
							</Box>
						</Flex>

						<Flex gap="32px" flexWrap="wrap">
							<Box flex="1" minWidth="300px">
								<Form.Item
									label="Capacity (in tonnes)"
									name="capacity"
									rules={[
										{ required: true, message: 'Please enter capacity' },
										{
											type: 'number',
											min: 0.01,
											max: 999.99,
											message: 'Capacity must be between 0.01 and 999.99',
											transform: (value) => Number(value),
										},
									]}
								>
									<Input
										placeholder="120"
										size="large"
										type="number"
										step="0.01"
										min="0.01"
										max="999.99"
										disabled={!isEditMode}
									/>
								</Form.Item>
							</Box>

							<Box flex="1" minWidth="300px">
								<Form.Item
									label="Length (in feet)"
									name="length"
									rules={[
										{ required: true, message: 'Please enter length' },
										{
											type: 'number',
											min: 0.01,
											max: 999.99,
											message: 'Length must be between 0.01 and 999.99',
											transform: (value) => Number(value),
										},
									]}
								>
									<Input
										placeholder="64"
										size="large"
										type="number"
										step="0.01"
										min="0.01"
										max="999.99"
										disabled={!isEditMode}
									/>
								</Form.Item>
							</Box>
						</Flex>

						<Flex gap="32px" flexWrap="wrap">
							<Box flex="1" minWidth="300px">
								<Form.Item
									label="Temperature Controlled"
									name="tempControlled"
									rules={[
										{
											required: true,
											message: 'Please select temperature control',
										},
									]}
								>
									<Select
										placeholder="Select"
										size="large"
										getPopupContainer={(trigger) => trigger.parentNode}
										disabled={!isEditMode}
									>
										<Option value="true">Yes</Option>
										<Option value="false">No</Option>
									</Select>
								</Form.Item>
							</Box>

							<Box flex="1" minWidth="300px">
								<Form.Item label="Driver Mode" name="driverMode">
									<Select
										placeholder="Select"
										size="large"
										getPopupContainer={(trigger) => trigger.parentNode}
										disabled={!isEditMode}
									>
										<Option value="Manual">Manual</Option>
										<Option value="Automatic">Automatic</Option>
									</Select>
								</Form.Item>
							</Box>
						</Flex>

						<Flex gap="32px" flexWrap="wrap">
							<Box flex="1" minWidth="300px">
								<Form.Item label="Fuel Type" name="fuelType">
									<Select
										placeholder="Select"
										size="large"
										getPopupContainer={(trigger) => trigger.parentNode}
										disabled={!isEditMode}
									>
										<Option value="Petrol">Petrol</Option>
										<Option value="Diesel">Diesel</Option>
										<Option value="Electric">Electric</Option>
										<Option value="CNG">CNG</Option>
										<Option value="Hybrid">Hybrid</Option>
									</Select>
								</Form.Item>
							</Box>
						</Flex>

						<ButtonContainer>
							<CancelButton onClick={handleCancelEdit} disabled={!isEditMode}>
								Cancel
							</CancelButton>
							<SaveButton onClick={handleSubmit} disabled={!isEditMode || loading}>
								{loading ? 'Saving...' : 'Save Changes'}
							</SaveButton>
						</ButtonContainer>
					</StyledForm>
				</FormSection>
			</ContentWrapper>
		</PageContainer>
	);
}

export default EditVehicleTypePageV2;
